// API service for meetings
import { post, del, fetcher } from '../../../services/api';
import type {
  MeetingsApiRequest,
  MeetingsApiResponse,
  MeetingsResult,
  PinMeetingRequest,
  PinMeetingApiRequest,
  PinMeetingApiResponse,
  UnpinMeetingApiResponse,
  PinMeetingResponse,
  UnpinMeetingResponse,
  PinnedMeetingsApiResponse,
  PinnedMeetingsResult,
  MeetingConflictsApiResponse,
  GetMeetingApiResponse,
  Meeting,
  MeetingApi,
  MeetingAttendeeApi,
  FreeSlot,
  FreeSlotApi,
} from '../types/meetings';
import type {
  SendMeetingReminderRequest,
  SendReminderApiRequest,
  SendReminderApiResponse,
  SendMeetingReminderResponse,
} from '../types/reminders';
import { transformSendReminderResponse } from '../types/reminders';

/**
 * Convert API free slot to domain free slot
 */
const toFreeSlotDomain = (apiFreeSlot: FreeSlotApi, calendarEmail: string, calendarName: string): FreeSlot => ({
  start: new Date(apiFreeSlot.start),
  end: new Date(apiFreeSlot.end),
  durationMinutes: apiFreeSlot.duration_minutes,
  calendarEmail,
  calendarName,
});

/**
 * Convert API meeting to domain meeting
 */
const toMeetingDomain = (apiMeeting: MeetingApi, calendarEmail: string, calendarName: string): Meeting => ({
  id: apiMeeting.id,
  calendarEmail,
  calendarName,
  subject: apiMeeting.subject,
  description: apiMeeting.description,
  bodyPreview: apiMeeting.body_preview,
  startDate: new Date(apiMeeting.start),
  endDate: new Date(apiMeeting.end),
  location: apiMeeting.location,
  isOrganiser: apiMeeting.is_organiser,
  isAllDay: apiMeeting.is_all_day,
  isRecurring: apiMeeting.is_recurring,
  bookingId: apiMeeting.booking_id,
  roamerNodeId: apiMeeting.roamer_node_id,
  roamerSpaceId: apiMeeting.roamer_space_id,
  isPrivate: apiMeeting.is_private,
  isCancelled: apiMeeting.is_cancelled,
  hasConflict: apiMeeting.conflict_count > 0,
  conflictCount: apiMeeting.conflict_count,
  conflictingMeetingIds: apiMeeting.conflicting_meeting_ids,
  myResponseStatus: apiMeeting.my_response_status,
  attendees:
    apiMeeting.attendees?.map((attendee: MeetingAttendeeApi) => ({
      id: attendee.email,
      name: attendee.name,
      email: attendee.email,
      responseStatus: attendee.response_status,
      isRequired: attendee.is_required,
      lastReminderSentAt: attendee.reminder_info?.last_reminder_sent_at
        ? new Date(attendee.reminder_info.last_reminder_sent_at)
        : null,
      reminderCount: attendee.reminder_info?.reminder_count ?? 0,
    })) ?? null,
  organiserName: apiMeeting.organiser_name,
  organiserEmail: apiMeeting.organiser_email,
  onlineMeetingUrl: apiMeeting.online_meeting_url,
  isOutOfOffice: apiMeeting.is_out_of_office,
  hasTentativeAttendees: apiMeeting.has_tentative_attendees,
  hasPendingAttendees: apiMeeting.has_pending_attendees,
  hasDeclinedAttendees: apiMeeting.has_declined_attendees,
  lastReminderSentAt: apiMeeting.last_reminder_sent_at ? new Date(apiMeeting.last_reminder_sent_at) : null,
  reminderCount: apiMeeting.reminder_count ?? 0,
  isPinned: apiMeeting.is_pinned,
  pinnedAt: apiMeeting.pinned_at ? new Date(apiMeeting.pinned_at) : null,
  categories: apiMeeting.categories ?? null,
});

/**
 * Convert API response to domain model
 */
const toMeetingsDomain = (apiResponse: MeetingsApiResponse, calendarNameMap: Map<string, string>): MeetingsResult => {
  const calendarEmails = apiResponse.calendars.map((cal) => cal.calendar_email);

  const allMeetings = apiResponse.calendars.flatMap((calendar) => {
    const calendarName = calendarNameMap.get(calendar.calendar_email) || calendar.calendar_email;

    return calendar.meetings.map((apiMeeting) => toMeetingDomain(apiMeeting, calendar.calendar_email, calendarName));
  });

  const allFreeSlots = apiResponse.calendars.flatMap((calendar) => {
    const calendarName = calendarNameMap.get(calendar.calendar_email) || calendar.calendar_email;

    return (calendar.free_slots || []).map((apiFreeSlot) =>
      toFreeSlotDomain(apiFreeSlot, calendar.calendar_email, calendarName),
    );
  });

  return {
    calendarEmails,
    startDate: apiResponse.start_date,
    endDate: apiResponse.end_date,
    meetings: allMeetings,
    freeSlots: allFreeSlots,
    totalMeetings: apiResponse.total_meetings,
  };
};

export const meetingsService = {
  /**
   * Fetch meetings for a calendar within a date range
   * @param calendarNameMap - Map of calendar email to display name
   */
  getMeetings: async (
    request: MeetingsApiRequest,
    calendarNameMap: Map<string, string> = new Map(),
  ): Promise<MeetingsResult> => {
    const response = await post<MeetingsApiResponse>('/api/v1/diary-management/meetings', request, { timeout: 45000 });

    const result = toMeetingsDomain(response, calendarNameMap);

    return result;
  },

  /**
   * Send reminder emails to meeting attendees
   */
  async sendReminder(request: SendMeetingReminderRequest): Promise<SendMeetingReminderResponse> {
    const apiRequest: SendReminderApiRequest = {
      recipient_emails: request.recipientEmails,
      body_content: request.bodyContent,
      calendar_owner_email: request.calendarOwnerEmail,
    };

    const response = await post<SendReminderApiResponse>(
      `/api/v1/diary-management/meetings/${request.meetingId}/send-reminders`,
      apiRequest,
    );

    return transformSendReminderResponse(response);
  },

  /**
   * Pin a meeting
   */
  async pinMeeting(meetingId: string, request: PinMeetingRequest): Promise<PinMeetingResponse> {
    const apiRequest: PinMeetingApiRequest = {
      meeting_start: request.meetingStart.toISOString(),
      calendar_owner_email: request.calendarOwnerEmail,
    };

    const response = await post<PinMeetingApiResponse>(
      `/api/v1/diary-management/meetings/${meetingId}/pin`,
      apiRequest,
    );

    return {
      meetingId: response.meeting_id,
      isPinned: response.is_pinned,
      pinnedAt: new Date(response.pinned_at),
    };
  },

  /**
   * Unpin a meeting
   */
  async unpinMeeting(meetingId: string): Promise<UnpinMeetingResponse> {
    const response = await del<UnpinMeetingApiResponse>(`/api/v1/diary-management/meetings/${meetingId}/pin`);

    return {
      meetingId: response.meeting_id,
      isPinned: response.is_pinned,
    };
  },

  /**
   * Get all pinned meetings grouped by calendar owner
   * @param calendarNameMap - Map of calendar email to display name
   */
  async getPinnedMeetings(calendarNameMap: Map<string, string> = new Map()): Promise<PinnedMeetingsResult> {
    const response = await fetcher<PinnedMeetingsApiResponse>('/api/v1/diary-management/pinned-meetings');

    // Handle empty or malformed response
    if (!response || !response.calendars) {
      return {
        pinnedByCalendar: [],
        totalPinnedMeetings: 0,
      };
    }

    // Transform API response to domain model
    const pinnedByCalendar = response.calendars.map((calendar) => {
      const calendarName = calendarNameMap.get(calendar.calendar_email) || calendar.calendar_email;

      return {
        calendarOwnerEmail: calendar.calendar_email,
        calendarOwnerName: calendarName,
        meetings: calendar.meetings
          .map((apiMeeting: MeetingApi) => toMeetingDomain(apiMeeting, calendar.calendar_email, calendarName))
          .sort((a: Meeting, b: Meeting) => {
            if (!a.pinnedAt) return 1;
            if (!b.pinnedAt) return -1;
            return b.pinnedAt.getTime() - a.pinnedAt.getTime();
          }),
      };
    });

    return {
      pinnedByCalendar,
      totalPinnedMeetings: response.total_pinned || 0,
    };
  },

  /**
   * Get conflicting meetings for a specific meeting
   */
  async getMeetingConflicts(meetingId: string, calendarOwnerEmail: string): Promise<Meeting[]> {
    const url = `/api/v1/diary-management/meetings/${meetingId}/conflicts?calendar_owner_email=${encodeURIComponent(calendarOwnerEmail)}`;
    const response = await fetcher<MeetingConflictsApiResponse>(url);

    return response.conflicting_meetings.map((apiMeeting: MeetingApi) =>
      toMeetingDomain(apiMeeting, response.calendar_owner_email, response.calendar_owner_name),
    );
  },

  /**
   * Get a single meeting by ID
   */
  async getMeeting(meetingId: string, calendarOwnerEmail?: string): Promise<Meeting> {
    const params = calendarOwnerEmail ? `?calendar_owner_email=${encodeURIComponent(calendarOwnerEmail)}` : '';
    const response = await fetcher<GetMeetingApiResponse>(
      `/api/v1/diary-management/meetings/${encodeURIComponent(meetingId)}${params}`,
    );

    return toMeetingDomain(response.meeting, response.calendar_owner_email, response.calendar_owner_email);
  },
};
